export type ThemePreference = "dark" | "light";

export const THEME_STORAGE_KEY = "tktic_theme";
const LEGACY_THEME_STORAGE_KEY = "replay_theme";
const DEFAULT_THEME: ThemePreference = "dark";

function isThemePreference(value: string | null): value is ThemePreference {
  return value === "dark" || value === "light";
}

function storage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function getInitialTheme(): ThemePreference {
  const store = storage();
  if (!store) return DEFAULT_THEME;

  const current = store.getItem(THEME_STORAGE_KEY);
  if (isThemePreference(current)) return current;

  const legacy = store.getItem(LEGACY_THEME_STORAGE_KEY);
  if (isThemePreference(legacy)) {
    try {
      store.setItem(THEME_STORAGE_KEY, legacy);
    } catch {
      // Ignore private-browsing / quota failures; the DOM still gets themed.
    }
    return legacy;
  }

  return DEFAULT_THEME;
}

export function applyTheme(theme: ThemePreference): ThemePreference {
  if (typeof document !== "undefined") {
    document.documentElement.dataset.theme = theme;
  }
  return theme;
}

export function setTheme(theme: ThemePreference): ThemePreference {
  const store = storage();
  if (store) {
    try {
      store.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // Storage failure should not prevent an in-session theme change.
    }
  }
  return applyTheme(theme);
}

export function toggleTheme(current: ThemePreference = getInitialTheme()): ThemePreference {
  return setTheme(current === "dark" ? "light" : "dark");
}

export function initializeThemePreference(): ThemePreference {
  return applyTheme(getInitialTheme());
}
